import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

// TRANSLATION
import { useTranslation } from 'react-i18next';

// HELPERS
import _ from 'lodash';
import { parse, stringify } from 'qs';
import getFields from '../../../Helpers/getters';
import convertToApiDate from '../../../Helpers/date';
import { update } from '../../../Helpers/objectOperations';

// CONFIG
import navigationIcons from '../../../config/navigationIcons';

// HOOKS
import useDebounce from '../../../Hooks/useDebounce';
import useDeleteEntity from '../../../Hooks/useDeleteEntity';
import useUpdateEffect from '../../../Hooks/useUpdateEffect';

// CONTEXTS
import ApplicationContext from '../../Context/ApplicationContext';

// COMPONENTS
import Create from '../../Components/Buttons/Create';
import DeleteConfirmModal from '../../Components/Delete/DeleteConfirmModal';
import DeleteEntityQuestion from '../../Components/Delete/DeleteEntityQuestion';
import Paginator from '../../Components/Paginator/Paginator';
import Table from '../../Components/Table';

const List = ({
    resource,
    service,
    search: SearchForm = null,
    readonly = false,
    deleteOnly = false,
    children,
    showUnreadInBold = false,
}) => {
    // CONTEXTS
    const setPageInfo = useContext(ApplicationContext);
    const { t } = useTranslation();

    // STATES
    const [isLoading, setIsLoading] = useState(false);
    const [fields] = useState(getFields(children));
    const { page = 1 } = useParams();
    const [list, setList] = useState({
        current: Number(page),
        last: 0,
        total: 0,
        entities: [],
        params: parse(window.location.search.replace('?', '')),
    });

    let url = resource;
    if (window.location.pathname.includes('categories')) {
        url = 'categories';
    }

    const params = parse(window.location.search.replace('?', ''));

    const newParameters = Object.keys(params).reduce((acc, key) => {
        if (key.includes('user.username')) {
            if (params['user.username']) {
                return { ...acc, 'filter[username]': params[key] };
            }
            return { ...acc };
        }
        if (key.includes('product.name')) {
            if (params['product.name']) {
                return { ...acc, 'filter[product_name]': params[key] };
            }
            return acc;
        }
        if (key.includes('filter.status')) {
            if (params['filter.status']) {
                return { ...acc, 'filter[status]': params[key].toLowerCase() };
            }
        }
        if (key.includes('filter_search')) {
            if (params.filter_search) {
                return { ...acc, 'filter[search]': params[key] };
            }
        }
        if (key.includes('published_from')) {
            if (params.published_from) {
                return {
                    ...acc,
                    'filter[created_at][min]': convertToApiDate(params[key]),
                };
            }
        }
        if (key.includes('published_to')) {
            if (params.published_to) {
                return {
                    ...acc,
                    'filter[created_at][max]': convertToApiDate(params[key]),
                };
            }
        }
        if (key.includes('sold_from')) {
            if (params.sold_from) {
                return {
                    ...acc,
                    'filter[purchased_at][min]': convertToApiDate(params[key]),
                };
            }
        }
        if (key.includes('sold_to')) {
            if (params.sold_to) {
                return {
                    ...acc,
                    'filter[purchased_at][max]': convertToApiDate(params[key]),
                };
            }
        }

        return acc;
    }, {});
    url = stringify(newParameters) ? `${url}?${stringify(newParameters)}` : url;

    // EVENTS
    const updateSearch = useDebounce(500, (newParams) => {
        const historyPath = resource.startsWith('categories-')
            ? 'categories'
            : resource;

        const sanitizedParams = Object.fromEntries(
            Object.entries(newParams).filter(
                ([, value]) => value !== '' && value != null,
            ),
        );

        if (historyPath === 'categories' && !sanitizedParams.type) {
            const typeFromResource = resource.startsWith('categories-')
                ? resource.replace('categories-', '')
                : null;

            if (typeFromResource) {
                sanitizedParams.type = typeFromResource;
            }
        }

        if (!_.isEqual(list.params, sanitizedParams)) {
            window.history.replaceState(
                null,
                '',
                `/${historyPath}?${stringify(sanitizedParams)}`,
            );

            update({ current: 1, params: sanitizedParams }, setList);
        }
    });

    const paginate = () => {
        setIsLoading(true);
        const additionalParams = url.startsWith('reviews')
            ? { 'filter[type]': 'product' }
            : {};
        service
            .list(url, list.current, 25, {
                ...list.params,
                ...additionalParams,
            })
            .then((response) => {
                const entities = response?.data ?? [];
                const meta = response?.meta;

                const isPaginated = !!meta;

                update(
                    {
                        current: isPaginated ? meta.current_page : 1,
                        last: isPaginated ? meta.last_page : 1,
                        total: isPaginated ? meta.total : entities.length,
                        entities,
                    },
                    setList,
                );
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    // DELETE
    const {
        markedForDeletion,
        markForDeletion,
        clearMarkedForDeletion,
        confirmDelete,
        error,
        isDeleting,
    } = useDeleteEntity({ service, resource, onSuccess: paginate });

    // SIDE EFFECTS
    useEffect(() => {
        setPageInfo({
            title: t(`${resource}.${resource}`),
            icon: navigationIcons[resource],
        });
    }, []);

    useEffect(() => {
        paginate();
    }, [list.current, list.params]);

    useUpdateEffect(() => {
        update({ current: Number(page) }, setList);
    }, [page]);

    return (
        <>
            {SearchForm && (
                <SearchForm
                    resource={resource}
                    entity={list.params}
                    service={service}
                    onChange={updateSearch}
                />
            )}

            {isLoading && <p>Loading...</p>}
            {!isLoading && (
                <Table
                    entities={list.entities}
                    fields={fields}
                    data={{ resource, service }}
                    remove={markForDeletion}
                    showUnreadInBold={showUnreadInBold}
                />
            )}
            {list.last > 1 && (
                <Paginator resource={resource} pagination={list} />
            )}
            {!readonly && !deleteOnly && <Create resource={resource} />}

            {(!readonly || deleteOnly) && (
                <DeleteConfirmModal
                    isOpen={!!markedForDeletion}
                    onRequestClose={clearMarkedForDeletion}
                    onConfirm={confirmDelete}
                    error={error}
                    isDeleting={isDeleting}
                >
                    {markedForDeletion && (
                        <DeleteEntityQuestion entity={markedForDeletion} />
                    )}
                </DeleteConfirmModal>
            )}
        </>
    );
};

export default List;

List.propTypes = {
    /**
     * Type of resource
     */
    resource: PropTypes.string.isRequired,
    /**
     * Type of service
     */
    service: PropTypes.object.isRequired,
    /**
     * Type of search
     */
    search: PropTypes.func,
    /**
     * Type of readonly
     */
    readonly: PropTypes.bool.isRequired,
    /**
     * Type of deleteonly
     */
    deleteOnly: PropTypes.bool.isRequired,
    /**
     * Type of children
     */
    children: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.node,
    ]).isRequired,
    /**
     * Type of showUnreadInBold
     */
    showUnreadInBold: PropTypes.bool,
};

List.defaultProps = {
    search: null,
    showUnreadInBold: false,
};
